import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import type { Card, Member } from '@/types/entities'

/**
 * Website-notification choices without a schema migration.
 *
 * These preferences are deliberately browser-local. Slack preferences continue to use the
 * existing server-backed member columns; the website switches never call an API and therefore
 * require no database/schema change. A workspace + member scoped key prevents a shared browser
 * from leaking one person's choices into another account or workspace.
 */
export interface WebsiteNotificationPreferences {
  notifyWebsite: boolean
  notifyWebsiteNewClients: boolean
  notifyWebsiteNewProjects: boolean
  notifyWebsitePendingApprovals: boolean
  notifyWebsiteAssignedWork: boolean
  notifyWebsiteAssignedByMeStatus: boolean
  notifyWebsiteTeamNewWork: boolean
  notifyWebsiteTeamWorkStatus: boolean
  notifyWebsiteSupportAssigned: boolean
  notifyWebsiteSupportTeamNew: boolean
}

export type WebsiteNotificationPreferenceKey = keyof WebsiteNotificationPreferences

export const DEFAULT_WEBSITE_NOTIFICATION_PREFERENCES: WebsiteNotificationPreferences = {
  notifyWebsite: true,
  notifyWebsiteNewClients: true,
  notifyWebsiteNewProjects: true,
  notifyWebsitePendingApprovals: true,
  notifyWebsiteAssignedWork: true,
  notifyWebsiteAssignedByMeStatus: true,
  notifyWebsiteTeamNewWork: true,
  notifyWebsiteTeamWorkStatus: true,
  notifyWebsiteSupportAssigned: true,
  notifyWebsiteSupportTeamNew: true,
}

const STORAGE_PREFIX = 'tapwork:website-notifications:v1'
const CHANGE_EVENT = 'tapwork:website-notifications-changed'

function keyFor(workspaceSlug: string, userId: string): string {
  return `${STORAGE_PREFIX}:${encodeURIComponent(workspaceSlug)}:${encodeURIComponent(userId)}`
}

function readRaw(key: string): string {
  if (typeof window === 'undefined') return ''
  try {
    return window.localStorage.getItem(key) ?? ''
  } catch {
    // Privacy modes can block localStorage. Defaults keep the bell usable rather than failing
    // the entire shell/settings route.
    return ''
  }
}

function parse(raw: string): WebsiteNotificationPreferences {
  if (!raw) return DEFAULT_WEBSITE_NOTIFICATION_PREFERENCES
  try {
    const parsed = JSON.parse(raw) as Partial<Record<WebsiteNotificationPreferenceKey, unknown>>
    const bool = (key: WebsiteNotificationPreferenceKey) =>
      typeof parsed[key] === 'boolean'
        ? (parsed[key] as boolean)
        : DEFAULT_WEBSITE_NOTIFICATION_PREFERENCES[key]
    return {
      notifyWebsite: bool('notifyWebsite'),
      notifyWebsiteNewClients: bool('notifyWebsiteNewClients'),
      notifyWebsiteNewProjects: bool('notifyWebsiteNewProjects'),
      notifyWebsitePendingApprovals: bool('notifyWebsitePendingApprovals'),
      notifyWebsiteAssignedWork: bool('notifyWebsiteAssignedWork'),
      notifyWebsiteAssignedByMeStatus: bool('notifyWebsiteAssignedByMeStatus'),
      notifyWebsiteTeamNewWork: bool('notifyWebsiteTeamNewWork'),
      notifyWebsiteTeamWorkStatus: bool('notifyWebsiteTeamWorkStatus'),
      notifyWebsiteSupportAssigned: bool('notifyWebsiteSupportAssigned'),
      notifyWebsiteSupportTeamNew: bool('notifyWebsiteSupportTeamNew'),
    }
  } catch {
    return DEFAULT_WEBSITE_NOTIFICATION_PREFERENCES
  }
}

function subscribe(key: string, callback: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined

  const onStorage = (event: StorageEvent) => {
    if (event.key === key) callback()
  }
  const onLocalChange = (event: Event) => {
    const detail = (event as CustomEvent<{ key?: string }>).detail
    if (detail?.key === key) callback()
  }

  window.addEventListener('storage', onStorage)
  window.addEventListener(CHANGE_EVENT, onLocalChange)
  return () => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener(CHANGE_EVENT, onLocalChange)
  }
}

function write(key: string, value: WebsiteNotificationPreferences): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { key } }))
  } catch {
    // A blocked/quota-exhausted localStorage must never break Settings or the notification bell.
    // The UI simply falls back to the all-on defaults on the next read.
  }
}

export function useWebsiteNotificationPreferences(workspaceSlug: string) {
  const { userId } = useIdentity()
  const storageKey = useMemo(() => keyFor(workspaceSlug, userId || 'anonymous'), [userId, workspaceSlug])

  const subscribeToKey = useCallback((callback: () => void) => subscribe(storageKey, callback), [storageKey])
  const getSnapshot = useCallback(() => readRaw(storageKey), [storageKey])
  const raw = useSyncExternalStore(subscribeToKey, getSnapshot, () => '')
  const preferences = useMemo(() => parse(raw), [raw])

  const update = useCallback(
    (patch: Partial<WebsiteNotificationPreferences>) => {
      const current = parse(readRaw(storageKey))
      write(storageKey, { ...current, ...patch })
    },
    [storageKey],
  )

  return { preferences, update }
}

type NotificationLike = {
  kind: string
  href: string | null
  cardId: string | null
}

function isWorkNotification(notification: NotificationLike): boolean {
  return Boolean(notification.cardId && notification.href?.startsWith('/work'))
}

function assignedTo(member: Member, card: Card): boolean {
  return card.ownerId === member.id || card.assigneeIds.includes(member.id)
}

function sharesTeam(member: Member, card: Card): boolean {
  if (member.teamIds.length === 0) return false
  const cardTeams = new Set([...(card.teamIds ?? []), ...(card.teamPathIds ?? [])])
  return member.teamIds.some((id) => cardTeams.has(id))
}

/**
 * Browser-side equivalent of the existing Slack category policy.
 *
 * The server still records the ordinary in-app row. This decides whether this browser shows it
 * in the bell/toast. For work/team categories, callers can supply the Work Item + current member
 * so the same "assigned by me" / "my team" distinctions used by Slack remain available without
 * adding any member columns. If related data cannot be loaded, it fails open rather than hiding
 * a potentially important notification by accident.
 */
export function shouldShowWebsiteNotification(
  notification: NotificationLike,
  preferences: WebsiteNotificationPreferences,
  card?: Card | null,
  member?: Member | null,
): boolean {
  // The on-call L3 watch is its own explicit opt-in (Focus mode); website switches never mute it.
  if (notification.kind === 'support_l3_watch') return true
  if (!preferences.notifyWebsite) return false

  switch (notification.kind) {
    case 'new_client':
      return preferences.notifyWebsiteNewClients
    case 'new_project':
      return preferences.notifyWebsiteNewProjects
    case 'approval_requested':
      return preferences.notifyWebsitePendingApprovals
    case 'assigned':
      return preferences.notifyWebsiteAssignedWork
    case 'support_assigned':
      return preferences.notifyWebsiteSupportAssigned
    case 'support_team_assigned':
    case 'support_team_new':
      return preferences.notifyWebsiteSupportTeamNew
    case 'new_work': {
      if (!isWorkNotification(notification)) return true
      if (!card || !member) return preferences.notifyWebsiteTeamNewWork
      return !assignedTo(member, card) && sharesTeam(member, card) && preferences.notifyWebsiteTeamNewWork
    }
    case 'status_changed':
    case 'completed':
    case 'blocked':
    case 'sent_back': {
      if (!isWorkNotification(notification)) return true
      if (!card || !member) {
        return (
          preferences.notifyWebsiteAssignedByMeStatus || preferences.notifyWebsiteTeamWorkStatus
        )
      }

      if (card.createdBy === member.id) return preferences.notifyWebsiteAssignedByMeStatus
      if (!assignedTo(member, card) && sharesTeam(member, card)) {
        return preferences.notifyWebsiteTeamWorkStatus
      }

      // Match Slack's existing policy: specialized direct notices still arrive; the generic
      // status broadcast has no separate "all visible work" preference and is suppressed.
      return notification.kind !== 'status_changed'
    }
    default:
      // Mentions, reminders, request changes, resolved approvals, etc. are not among the seven
      // category switches and keep their existing behaviour while the master switch is on.
      return true
  }
}
